import { useEffect, useCallback, useRef } from 'react';
import logger from '../utils/logger';
import posthog from 'posthog-js';
import { EVENTS } from '../constants/events';

export const useAnalytics = () => {
  // User identification and properties
  const identifyUser = useCallback((userId, userProperties = {}) => {
    if (typeof window !== 'undefined') {
      posthog.identify(userId, userProperties);
    }
  }, []);

  const setUserProperties = useCallback((properties, propertiesSetOnce) => {
    if (typeof window !== 'undefined') {
      posthog.setPersonProperties(properties, propertiesSetOnce);
    }
  }, []);

  // Core event tracking functions
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (typeof window !== 'undefined') {
      logger.log('🔔 PostHog Event:', eventName, properties);
      posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
        source: 'pixley_web_app'
      });
    }
  }, []);

  // User Journey Events
  const trackSignUp = useCallback((method, success = true, error = null) => {
    trackEvent('user_signup', {
      signup_method: method, // 'email', 'google'
      success,
      error_message: error,
      signup_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackSignIn = useCallback((method, success = true, error = null) => {
    trackEvent('user_signin', {
      signin_method: method,
      success,
      error_message: error
    });
  }, [trackEvent]);

  const trackOnboardingStep = useCallback((step, action, data = {}) => {
    trackEvent('onboarding_step', {
      step, // 'welcome', 'tutorial', 'first_character'
      action, // 'started', 'completed', 'skipped', 'abandoned'
      ...data
    });
  }, [trackEvent]);

  const trackTutorialStep = useCallback((step, action, data = {}) => {
    trackEvent('tutorial_step', {
      step,
      action, // 'started', 'completed', 'skipped'
      ...data
    });
  }, [trackEvent]);

  // @deprecated — never wired. Keep for future onboarding funnel.
  const trackFirstAction = useCallback((actionType, timeSinceOnboarding = null, additionalData = {}) => {
    trackEvent('first_action_after_onboarding', {
      action_type: actionType, // 'watch_video', 'create_character', 'create_story', 'navigate_to_create', 'navigate_to_library'
      time_since_onboarding_seconds: timeSinceOnboarding,
      time_since_onboarding_minutes: timeSinceOnboarding ? Math.round(timeSinceOnboarding / 60) : null,
      ...additionalData
    });
  }, [trackEvent]);

  // @deprecated — never wired. Keep for future onboarding funnel.
  const trackPostOnboardingJourney = useCallback((hasWatched, hasCreatedCharacter, hasCreatedStory, timeSinceSignup = null) => {
    trackEvent('post_onboarding_journey', {
      has_watched_content: hasWatched,
      has_created_character: hasCreatedCharacter,
      has_created_story: hasCreatedStory,
      completed_actions: [
        hasWatched && 'watched',
        hasCreatedCharacter && 'created_character',
        hasCreatedStory && 'created_story'
      ].filter(Boolean),
      actions_completed_count: [hasWatched, hasCreatedCharacter, hasCreatedStory].filter(Boolean).length,
      time_since_signup_seconds: timeSinceSignup,
      time_since_signup_minutes: timeSinceSignup ? Math.round(timeSinceSignup / 60) : null
    });
  }, [trackEvent]);

  // Character Events — intent + success pair
  const trackCharacterCreationStarted = useCallback((data = {}) => {
    trackEvent(EVENTS.CHARACTER_CREATION_STARTED, {
      creation_method: data.method || 'unknown', // 'camera', 'upload', 'text', 'draw'
      character_name: data.name || null,
      has_image: !!data.hasImage,
    });
  }, [trackEvent]);

  const trackCharacterCreation = useCallback((characterData) => {
    trackEvent(EVENTS.CHARACTER_CREATED, {
      character_id: characterData.id,
      character_name: characterData.name,
      creation_method: characterData.creation_method,
      has_description: !!characterData.description,
      has_image: !!characterData.image_url,
    });
  }, [trackEvent]);

  // @deprecated — no call sites as of May 2026. Keep for future use.
  const trackCharacterEnhancement = useCallback((characterId, enhancementType, success = true) => {
    trackEvent('character_enhanced', {
      character_id: characterId,
      enhancement_type: enhancementType,
      success,
    });
  }, [trackEvent]);

  const trackCharacterAnimation = useCallback((characterId, success = true, duration = null) => {
    trackEvent(EVENTS.CHARACTER_ANIMATED, {
      character_id: characterId,
      success,
      generation_duration_seconds: duration,
      animation_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackCharacterEdit = useCallback((characterId, editType, success = true) => {
    trackEvent('character_edited', {
      character_id: characterId,
      edit_type: editType, // 'gemini_edit', 'manual_edit', 'description_change'
      success,
      edit_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Story & Episode Events
  const trackStoryCreation = useCallback((storyData) => {
    trackEvent(EVENTS.STORY_CREATED, {
      story_id: storyData.id,
      story_title: storyData.title,
      character_count: storyData.characters?.length || 0,
      has_description: !!storyData.description,
      is_storyverse: storyData.is_storyverse || false,
      is_first_story: storyData.is_first_story || false,
      story_count: storyData.story_count || null,
    });
  }, [trackEvent]);

  // @deprecated — episodes/shows pipeline disabled May 2026
  const trackEpisodeCreation = useCallback((episodeData) => {
    trackEvent('episode_created', {
      episode_id: episodeData.id,
      story_id: episodeData.story_id,
      episode_title: episodeData.title,
    });
  }, [trackEvent]);

  // Content Interaction Events
  const trackContentView = useCallback((contentType, contentId, viewDuration = null) => {
    trackEvent(EVENTS.CONTENT_VIEWED, {
      content_type: contentType, // 'character', 'story', 'episode', 'storyverse_content'
      content_id: contentId,
      view_duration_seconds: viewDuration,
      view_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackContentShare = useCallback((contentType, contentId, shareMethod = 'link') => {
    trackEvent(EVENTS.CONTENT_SHARED, {
      content_type: contentType,
      content_id: contentId,
      share_method: shareMethod, // 'link', 'social', 'copy'
      share_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Navigation & Engagement Events
  const trackPageView = useCallback((pageName, additionalData = {}) => {
    trackEvent(EVENTS.PAGE_VIEWED, {
      page_name: pageName,
      ...additionalData,
      page_view_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Error Events
  const trackError = useCallback((errorType, errorMessage, context = {}) => {
    trackEvent('error_occurred', {
      error_type: errorType,
      error_message: errorMessage,
      error_context: context,
    });
  }, [trackEvent]);

  // Business & Subscription Events
  const trackSubscriptionEvent = useCallback((action, planType = null, error = null) => {
    trackEvent('subscription_event', {
      action,
      plan_type: planType,
      success: !error,
      error_message: error,
    });
  }, [trackEvent]);

  const trackSubscriptionPurchased = useCallback((data = {}) => {
    trackEvent(EVENTS.SUBSCRIPTION_PURCHASED, {
      value: data.value,
      currency: data.currency || 'USD',
      plan: data.plan,
      billing: data.billing,
      transaction_id: data.transaction_id,
    });
  }, [trackEvent]);

  const trackStarsPurchased = useCallback((data = {}) => {
    trackEvent(EVENTS.STARS_PURCHASED, {
      value: data.value,
      currency: data.currency || 'USD',
      product_id: data.product_id,
      transaction_id: data.transaction_id,
    });
  }, [trackEvent]);

  // Session tracking
  const trackSessionStart = useCallback(() => {
    trackEvent(EVENTS.SESSION_STARTED, {
      session_start_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackSessionEnd = useCallback((sessionDuration) => {
    trackEvent(EVENTS.SESSION_ENDED, {
      session_duration_seconds: sessionDuration,
      session_end_timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Auth
  const trackSignOut = useCallback(() => {
    trackEvent('user_signout', {});
  }, [trackEvent]);

  const resetUser = useCallback(() => {
    if (typeof window !== 'undefined') {
      posthog.reset();
    }
  }, []);

  // Video events (replacing raw captures)
  const trackVideoWatched = useCallback((data) => {
    trackEvent('video_watched', {
      video_title: data.title,
      episode_id: data.episodeId,
      story_id: data.storyId,
      is_storyverse: data.isStoryverse,
      subscription_type: data.subscriptionType,
    });
  }, [trackEvent]);

  const trackStoryverseEpisodeWatched = useCallback((data) => {
    trackEvent('storyverse_episode_watched', {
      episode_title: data.title,
      episode_id: data.episodeId,
      subscription_type: data.subscriptionType,
    });
  }, [trackEvent]);

  const trackVideoMinutesWatched = useCallback((data) => {
    trackEvent('video_minutes_watched', {
      video_title: data.title,
      episode_id: data.episodeId,
      story_id: data.storyId,
      minutes_watched: data.minutesWatched,
      subscription_type: data.subscriptionType,
    });
  }, [trackEvent]);

  // Navigation
  const trackUpgradeButtonClicked = useCallback((location) => {
    trackEvent('upgrade_button_clicked', { location });
  }, [trackEvent]);

  // Audiobook events — intent + success pair
  const trackAudiobookCreationStarted = useCallback((data = {}) => {
    trackEvent(EVENTS.STORYBOOK_CREATION_STARTED, {
      character_count: data.characterCount || 0,
      reading_level: data.readingLevel,
      page_count: data.pageCount,
      has_description: !!data.description,
    });
  }, [trackEvent]);

  const trackAudiobookCreated = useCallback((data) => {
    trackEvent('audiobook_created', {
      audiobook_title: data.title,
      character_count: data.characterCount,
      voice_option: data.voiceOption,
      reading_level: data.readingLevel,
      page_count: data.pageCount,
    });
  }, [trackEvent]);

  const trackAudiobookPlayed = useCallback((audiobookId, title) => {
    trackEvent('audiobook_played', { audiobook_id: audiobookId, audiobook_title: title });
  }, [trackEvent]);

  const trackAudiobookPageTurned = useCallback((audiobookId, pageIndex, totalPages) => {
    trackEvent('audiobook_page_turned', { audiobook_id: audiobookId, page_index: pageIndex, total_pages: totalPages });
  }, [trackEvent]);

  const trackAudiobookCompleted = useCallback((audiobookId, title) => {
    trackEvent('audiobook_completed', { audiobook_id: audiobookId, audiobook_title: title });
  }, [trackEvent]);

  // Content actions
  const trackContentLiked = useCallback((contentType, contentId, liked) => {
    trackEvent('content_liked', { content_type: contentType, content_id: contentId, liked });
  }, [trackEvent]);

  const trackContentDownloaded = useCallback((contentType, contentId) => {
    trackEvent('content_downloaded', { content_type: contentType, content_id: contentId });
  }, [trackEvent]);

  const trackContentDeleted = useCallback((contentType, contentId) => {
    trackEvent('content_deleted', { content_type: contentType, content_id: contentId });
  }, [trackEvent]);

  const trackVisibilityToggled = useCallback((contentType, contentId, isPublic) => {
    trackEvent('visibility_toggled', { content_type: contentType, content_id: contentId, is_public: isPublic });
  }, [trackEvent]);

  // Payments / Conversion
  const trackPaywallShown = useCallback((contentType, starCost, currentStars) => {
    trackEvent('paywall_shown', { content_type: contentType, star_cost: starCost, current_stars: currentStars });
  }, [trackEvent]);

  const trackPaywallCtaClicked = useCallback((ctaType, contentType) => {
    trackEvent('paywall_cta_clicked', { cta_type: ctaType, content_type: contentType });
  }, [trackEvent]);

  const trackCheckoutStarted = useCallback((checkoutType, tier) => {
    trackEvent('checkout_started', { checkout_type: checkoutType, tier });
  }, [trackEvent]);

  const trackSubscriptionCancelled = useCallback((cancelImmediately) => {
    trackEvent('subscription_cancelled', { cancel_immediately: cancelImmediately });
  }, [trackEvent]);

  // Search
  const trackSearchSubmitted = useCallback((query) => {
    trackEvent('search_submitted', { query_length: query?.length || 0 });
  }, [trackEvent]);

  const trackSearchCleared = useCallback(() => {
    trackEvent('search_cleared', {});
  }, [trackEvent]);

  // Profiles
  const trackProfileSwitched = useCallback((fromProfileId, toProfileId) => {
    trackEvent('profile_switched', { from_profile_id: fromProfileId, to_profile_id: toProfileId });
  }, [trackEvent]);

  // Weekly challenge
  const trackWeeklyChallengeViewed = useCallback((theme, remainingDays) => {
    trackEvent('weekly_challenge_viewed', { theme, remaining_days: remainingDays });
  }, [trackEvent]);

  const trackWeeklyChallengeStoryClicked = useCallback((storyId) => {
    trackEvent('weekly_challenge_story_clicked', { story_id: storyId });
  }, [trackEvent]);

  return {
    // Core functions
    identifyUser,
    setUserProperties,
    trackEvent,
    resetUser,

    // User journey
    trackSignUp,
    trackSignIn,
    trackSignOut,
    trackOnboardingStep,
    trackTutorialStep,
    trackFirstAction,
    trackPostOnboardingJourney,

    // Characters
    trackCharacterCreationStarted,
    trackCharacterCreation,
    trackCharacterEnhancement,
    trackCharacterAnimation,
    trackCharacterEdit,

    // Stories & Episodes
    trackStoryCreation,
    trackEpisodeCreation,

    // Content interaction
    trackContentView,
    trackContentShare,
    trackContentLiked,
    trackContentDownloaded,
    trackContentDeleted,
    trackVisibilityToggled,

    // Video
    trackVideoWatched,
    trackStoryverseEpisodeWatched,
    trackVideoMinutesWatched,

    // Audiobooks
    trackAudiobookCreationStarted,
    trackAudiobookCreated,
    trackAudiobookPlayed,
    trackAudiobookPageTurned,
    trackAudiobookCompleted,

    // Navigation & engagement
    trackPageView,
    trackUpgradeButtonClicked,

    // Errors
    trackError,

    // Business metrics
    trackSubscriptionEvent,
    trackSubscriptionPurchased,
    trackStarsPurchased,
    trackCheckoutStarted,
    trackSubscriptionCancelled,
    trackPaywallShown,
    trackPaywallCtaClicked,

    // Search
    trackSearchSubmitted,
    trackSearchCleared,

    // Profiles
    trackProfileSwitched,

    // Weekly challenge
    trackWeeklyChallengeViewed,
    trackWeeklyChallengeStoryClicked,

    // Session tracking
    trackSessionStart,
    trackSessionEnd
  };
};

// Page tracking hook — fires ONCE per unique page name, survives remounts
const _trackedPages = new Set();
export const usePageTracking = (pageName) => {
  const { trackPageView } = useAnalytics();
  const hasFired = useRef(false);

  useEffect(() => {
    const key = `${pageName}_${window.location.pathname}`;
    if (pageName && !hasFired.current && !_trackedPages.has(key)) {
      _trackedPages.add(key);
      hasFired.current = true;
      trackPageView(pageName);
      // Clear after 2 seconds so navigating away and back still tracks
      setTimeout(() => _trackedPages.delete(key), 2000);
    }
  }, [pageName]); // eslint-disable-line react-hooks/exhaustive-deps
};

// Session tracking hook
export const useSessionTracking = () => {
  const { trackSessionStart, trackSessionEnd } = useAnalytics();

  useEffect(() => {
    const sessionStartTime = Date.now();
    trackSessionStart();

    const handleBeforeUnload = () => {
      const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
      trackSessionEnd(sessionDuration);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const sessionDuration = Math.round((Date.now() - sessionStartTime) / 1000);
      trackSessionEnd(sessionDuration);
    };
  }, [trackSessionStart, trackSessionEnd]);
};